import { useEffect, useMemo, useState } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined";
import { Box, IconButton, Popover, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
  type GridRowSelectionModel,
  type GridValidRowModel,
} from "@mui/x-data-grid";
import type { CustomDataTableProps } from "@components/DataTable/CustomDataTable.types";
import { dataGridSx, dataGridWrapperSx } from "@components/DataTable/styles";
import Input from "@components/Input/Input";
import OverflowTooltip from "@components/OverflowTooltip/OverflowTooltip";
import Select from "@components/Select/Select";
import TableFilter from "@components/TableFilters/TableFilters.tsx";
import { markNotificationsSeen } from "@services/notificationsApi";
import { runTransaction } from "@telemetry/runTransaction";
import { getTotalPages } from "@utils/pagination";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";
import { renderColumns } from "./columns";
import NoTableEntries from "./NoTableEntries";

const MARK_NOTIFICATIONS_SEEN_TRANSACTION_CONTEXT = {
  module: "notifications",
  screen: "Notifications",
  action: "mark_notifications_seen",
  entityType: "notification",
};

const getCellTooltipTitle = (params: GridRenderCellParams) => {
  const value = params.formattedValue ?? params.value;
  return value == null || value === "" ? undefined : String(value);
};

function TableCellOverflow({
  children,
  fallbackTitle,
}: {
  children: React.ReactNode;
  fallbackTitle?: string;
}) {
  return (
    <OverflowTooltip
      placement="top"
      title={(el) => el?.textContent?.trim() || fallbackTitle || ""}
      getTarget={(wrapperEl) => {
        const target = wrapperEl.querySelector(
          "[data-overflow-tooltip-target='true']",
        ) as HTMLElement | null;
        return (target?.firstElementChild as HTMLElement | null) || target;
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          minWidth: 0,
          height: "100%",
          fontWeight: "inherit",
        }}
      >
        <Box
          data-overflow-tooltip-target="true"
          sx={{
            "display": "block",
            "width": "100%",
            "minWidth": 0,
            "overflow": "hidden",
            "textOverflow": "ellipsis",
            "whiteSpace": "nowrap",
            "fontWeight": "inherit",
            "& > *": {
              display: "block",
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100%",
              fontWeight: "inherit",
            },
            "& .MuiChip-root": {
              maxWidth: "100%",
            },
            "& .MuiBadge-root": {
              maxWidth: "100%",
            },
            "& .MuiChip-label": {
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
          }}
        >
          {children}
        </Box>
      </Box>
    </OverflowTooltip>
  );
}

function withOverflowTooltip<T extends GridValidRowModel>(column: GridColDef<T>): GridColDef<T> {
  const nonOverflowFields = new Set(["action", "__action__", "__select"]);

  if (nonOverflowFields.has(String(column.field))) {
    return column;
  }

  const originalRenderCell = column.renderCell;

  return {
    ...column,
    display: column.display ?? "flex",
    renderCell: (params: GridRenderCellParams<T>) => {
      const content = originalRenderCell
        ? originalRenderCell(params)
        : (params.formattedValue ?? params.value ?? "-");

      return (
        <TableCellOverflow fallbackTitle={getCellTooltipTitle(params)}>{content}</TableCellOverflow>
      );
    },
  };
}

function withTextColumnMinWidth<T extends GridValidRowModel>(column: GridColDef<T>): GridColDef<T> {
  const nonTextFields = new Set(["action", "__action__", "__select", "status", "restrictions"]);

  if (nonTextFields.has(String(column.field))) {
    return column;
  }

  return {
    ...column,
    minWidth: Math.max(column.minWidth ?? 0, 150),
  };
}

const CustomDataTable = <T extends GridValidRowModel>({
  title,
  actionName,
  rows,
  headers,
  canPreview,
  onRowClick,
  onPageChange,
  testId,
  page,
  pageSize,
  totalCount,
  navigationLinkBase,
  interModulesNavigation,
  navigationParam,
  pageSizeOptions = [15, 20, 25, 50],
  filters,
  onApplyFilters,
  onClearFilters,
  availableFields,
  shouldFreezeExtraColumn,
  hideFilters = false,
  actionColumn,
  displaySubTitle = true,
  isNotificationTable = false,
  hasCheckboxes = false,
  onSelectionChange,
  rowSelectionModel,
  isRowSelectable,
  headerContent,
  disableVirtualization = true,
  isLoading = false,
}: CustomDataTableProps<T>) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const totalPages = getTotalPages(totalCount ?? rows.length, pageSize);
  const hasPages = totalPages > 0;
  const pageInput = hasPages ? page : 0;

  const [pageInputValue, setPageInputValue] = useState(String(pageInput));

  useEffect(() => {
    setPageInputValue(String(pageInput));
  }, [pageInput]);

  const getRowProperty = (row: any, path: string) =>
    path.split(".").reduce((acc: string, key: any) => acc?.[key], row) || row.id;

  const columns = useMemo(
    () =>
      renderColumns(headers, rows, theme, shouldFreezeExtraColumn).map((column) =>
        withTextColumnMinWidth(withOverflowTooltip(column)),
      ),
    [headers, rows, theme, shouldFreezeExtraColumn],
  );
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [popoverRowId, setPopoverRowId] = useState<string | number | null>(null);

  const commitPageInput = (rawValue: string) => {
    if (!hasPages) {
      setPageInputValue("0");
      return;
    }

    const trimmedValue = rawValue.trim();

    if (!trimmedValue) {
      setPageInputValue(String(page));
      return;
    }

    const numericValue = Number(trimmedValue);

    if (!Number.isFinite(numericValue)) {
      setPageInputValue(String(page));
      return;
    }

    const nextPage = Math.min(Math.max(1, Math.trunc(numericValue)), totalPages);
    setPageInputValue(String(nextPage));
    onPageChange?.(nextPage, pageSize);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setPopoverRowId(null);
  };
  const finalCols = useMemo(() => {
    let actionCol: GridColDef<T> | undefined;
    if (actionColumn) {
      actionCol = withOverflowTooltip({
        field: "__action__",
        headerName: t("common.action-column"),
        sortable: false,
        filterable: false,
        width: actionColumn.width ?? 120,
        align: "center",
        headerAlign: "center",
        disableColumnMenu: true,
        cellClassName: "freeze-right",
        headerClassName: "freeze-right",
        renderCell: (params: GridRenderCellParams<T, any>) => {
          const isActionEnabled = actionColumn.isActionEnabled
            ? actionColumn.isActionEnabled(params.row)
            : true;

          return (
            <Box>
              <IconButton
                aria-label="More actions"
                size="small"
                disabled={!isActionEnabled}
                onClick={(e) => {
                  e.stopPropagation();
                  setAnchorEl(e.currentTarget);
                  setPopoverRowId(params.row.id);
                }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <MoreVertOutlinedIcon />
              </IconButton>
              <Popover
                open={popoverRowId === params.row.id && Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handlePopoverClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
              >
                <Box p={2}>
                  {actionColumn.onActionClick && (
                    <Typography
                      variant="body2"
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        actionColumn.onActionClick?.(params.row);
                        handlePopoverClose();
                      }}
                    >
                      {actionColumn.actionTitle || t("common.view-details")}
                    </Typography>
                  )}
                  {actionColumn.onEditClick && (
                    <Typography
                      variant="body1"
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        actionColumn.onEditClick?.(params.row);
                        handlePopoverClose();
                      }}
                    >
                      {t("common.edit")}
                    </Typography>
                  )}
                  {actionColumn.onDeleteClick && (
                    <Typography
                      variant="body1"
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        actionColumn.onDeleteClick?.(params.row);
                        handlePopoverClose();
                      }}
                    >
                      {t("common.delete")}
                    </Typography>
                  )}
                </Box>
              </Popover>
            </Box>
          );
        },
      });
    }
    return actionCol ? [...columns, actionCol] : columns;
  }, [columns, actionColumn, anchorEl, popoverRowId]);

  return (
    <Box sx={{ width: "100%" }} data-testid={testId}>
      {rows.length || filters.length || (isLoading && !filters.length) ? (
        <>
          {(title || !hideFilters) && displaySubTitle && (
            <Box
              display="flex"
              justifyContent={hideFilters ? "flex-start" : "space-between"}
              alignItems="center"
              mb={2}
            >
              {headerContent ? (
                <Box data-testid="table-title">{headerContent}</Box>
              ) : (
                <Typography
                  data-testid="table-title"
                  variant="h6"
                  sx={{ fontWeight: theme.typography.fontWeightLight }}
                >
                  {title}
                </Typography>
              )}

              {!hideFilters && (
                <TableFilter
                  filters={filters}
                  onApplyFilters={onApplyFilters}
                  onClearFilters={onClearFilters}
                  availableFields={availableFields}
                />
              )}
            </Box>
          )}

          <Box sx={dataGridWrapperSx}>
            <DataGrid
              rows={rows}
              columns={finalCols}
              checkboxSelection={hasCheckboxes}
              {...(hasCheckboxes && rowSelectionModel != null ? { rowSelectionModel } : {})}
              isRowSelectable={
                hasCheckboxes && isRowSelectable
                  ? (params) => isRowSelectable(params.row as T)
                  : undefined
              }
              onRowSelectionModelChange={(newSelectionModel: GridRowSelectionModel) => {
                if (!onSelectionChange) return;

                // MUI X Data Grid v7+ uses { type: 'include' | 'exclude', ids: Set }
                const model = newSelectionModel as
                  | { type?: string; ids?: Set<unknown> | (string | number)[] }
                  | (string | number)[]
                  | null
                  | undefined;

                let selected: T[];
                if (Array.isArray(model)) {
                  const ids = model as (string | number)[];
                  selected = rows.filter((r: any) => ids.includes((r as any).id)) as T[];
                } else if (model && typeof model === "object" && "ids" in model) {
                  const idsSet = model.ids;
                  const ids =
                    idsSet instanceof Set
                      ? (Array.from(idsSet) as (string | number)[])
                      : Array.isArray(idsSet)
                        ? (idsSet as (string | number)[])
                        : [];
                  const isExclude = model.type === "exclude";
                  selected = rows.filter((r: any) => {
                    const id = (r as any).id;
                    const inSet = ids.includes(id);
                    return isExclude ? !inSet : inSet;
                  }) as T[];
                } else {
                  selected = [];
                }

                onSelectionChange(selected);
              }}
              getRowClassName={(params) => {
                const r = params.row as Record<string, unknown>;
                if (!Object.prototype.hasOwnProperty.call(r, "seen")) return "";
                return r.seen === true ? "row--seen" : "row--unseen";
              }}
              hideFooter
              disableColumnMenu
              disableColumnSorting
              disableColumnResize
              disableVirtualization={disableVirtualization}
              getRowId={(row) => row.id}
              pagination
              paginationMode="server"
              rowCount={totalCount}
              localeText={{
                noRowsLabel: t("common.empty-table.no-rows"),
              }}
              pageSizeOptions={pageSizeOptions}
              paginationModel={{ page: page - 1, pageSize }}
              onPaginationModelChange={({ page: pg, pageSize: ps }) => {
                const nextPage = pg + 1;

                if (!hasPages || (nextPage === page && ps === pageSize)) return;
                if (nextPage < 1 || nextPage > totalPages) return;

                onPageChange?.(nextPage, ps);
              }}
              onRowClick={async (params, e) => {
                if (onRowClick) {
                  onRowClick(params.row);
                  return;
                }

                if (canPreview) {
                  let navParam = params.row.id;
                  if (navigationParam) {
                    navParam = getRowProperty(params.row, navigationParam);
                  }
                  if (navigationLinkBase) {
                    navigate(`${navigationLinkBase}/${navParam}`);
                  } else {
                    navigate(`${navParam}`);
                  }
                  if (isNotificationTable) {
                    await runTransaction(
                      {
                        ...MARK_NOTIFICATIONS_SEEN_TRANSACTION_CONTEXT,
                        entityId: String(params.row.id),
                      },
                      (transactionId) =>
                        markNotificationsSeen({
                          service: "bff-notification",
                          ids: [String(params.row.id)],
                          ...(transactionId ? { transactionId } : {}),
                        }),
                    );
                  }

                  if (interModulesNavigation) {
                    e.preventDefault();
                    const nav =
                      typeof interModulesNavigation === "function"
                        ? interModulesNavigation(params.row)
                        : interModulesNavigation;
                    navigate(
                      `${nav.pathToModule}/${getRowProperty(
                        params.row,
                        nav.targetModuleResourceBy,
                      )}/${nav.pathToSubModule}/${getRowProperty(
                        params.row,
                        nav.targetSubModuleResourceBy,
                      )}`,
                      { replace: true },
                    );
                  }
                }
              }}
              sx={dataGridSx}
            />
          </Box>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ pt: 2, px: 1 }}
            data-testid={`${testId}-footer`}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body1">Rows per page</Typography>
              <Select
                testId="rows-per-page-select"
                value={String(pageSize)}
                placeholder="Select"
                style={{ width: 90 }}
                options={pageSizeOptions.map((opt) => ({ value: opt, label: String(opt) }))}
                onChange={(value) => {
                  const newSize = Number(value);
                  onPageChange?.(1, newSize);
                }}
              />
              <Typography variant="body1">from {totalCount ?? rows.length}</Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={1}>
              <IconButton
                size="small"
                data-testid="pagination-prev"
                disabled={!hasPages || page <= 1}
                onClick={() => page > 1 && onPageChange?.(page - 1, pageSize)}
              >
                <ChevronLeftIcon />
              </IconButton>

              <Input
                size="small"
                value={pageInputValue}
                type="number"
                testId="pagination-page-input"
                disabled={!hasPages}
                sx={{
                  "& .MuiInputBase-root": {
                    height: "32px",
                    minWidth: "32px",
                    width: "fit-content",
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: theme.palette.text.brand,
                  },
                  "& .MuiInputBase-input": {
                    padding: 0,
                    fontWeight: theme.typography.fontWeightMedium,
                    textAlign: "center",
                    width: "3ch",
                    boxSizing: "border-box",
                  },
                  "& input[type=number]": {
                    "MozAppearance": "textfield",
                    "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
                      WebkitAppearance: "none",
                      margin: 0,
                    },
                  },
                }}
                slotProps={{
                  htmlInput: {
                    min: hasPages ? 1 : 0,
                    max: totalPages,
                  },
                }}
                onChange={(e) => {
                  if (!hasPages) return;
                  setPageInputValue(e.target.value);
                }}
                onBlur={(e) => {
                  commitPageInput(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    commitPageInput((e.target as HTMLInputElement).value);
                  }
                }}
              />

              <IconButton
                size="small"
                data-testid="pagination-next"
                disabled={!hasPages || page >= totalPages}
                onClick={() => page < totalPages && onPageChange?.(page + 1, pageSize)}
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>
          </Box>
        </>
      ) : (
        <Box
          sx={{
            width: "100%",
            minHeight: 400,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <NoTableEntries
            dataName={title}
            actionName={actionName}
            descriptionComponents={{ strong: <strong /> }}
          />
        </Box>
      )}
    </Box>
  );
};

export default CustomDataTable;
